/**
 * PasswordInput.js — Password field with visibility toggle
 *
 * Wraps the standard Input component to add an eye/eye-off button
 * that switches the field between type="password" (masked) and
 * type="text" (visible).
 *
 * Why this exists:
 *   Users frequently mistype passwords in masked fields. A visibility
 *   toggle lets them verify what they've entered before submitting,
 *   reducing failed login/signup attempts.
 *
 * Usage:
 *   <PasswordInput value={pw} onChange={setPw} placeholder="..." />
 */

import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Eye, EyeOff } from 'lucide-react';

export default function PasswordInput({ value, onChange, className = '', ...props }) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="relative">
      <Input
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        className={`pr-10 ${className}`}
        {...props}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-ndor-ink-muted hover:text-ndor-ink transition-colors"
        aria-label={visible ? 'Hide password' : 'Show password'}
        tabIndex={-1}
        data-testid={`${props['data-testid'] || 'password'}-toggle`}
      >
        {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}
